// Client for the KryptaMail crypto-service (PQC E2E).
// The webmail calls this to seal (encrypt+sign) and open (verify+decrypt)
// messages. Served same-origin under /crypto behind the PQC proxy.

export class CryptoClient {
  constructor({ baseUrl = '/crypto' } = {}) {
    this.baseUrl = baseUrl;
  }

  async _post(path, body) {
    const res = await fetch(`${this.baseUrl}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || `crypto ${path} failed: ${res.status}`);
    return data;
  }

  async _get(path) {
    const res = await fetch(`${this.baseUrl}${path}`);
    if (!res.ok) throw new Error(`crypto ${path} failed: ${res.status}`);
    return res.json();
  }

  lookupRecipient(address) {
    return this._get(`/v1/directory/lookup?address=${encodeURIComponent(address)}`)
      .catch(() => null);
  }

  // Provision a PQC identity for `subject`: the crypto-service generates the
  // hybrid KEM + composite signing keypair, issues a cert from the internal CA,
  // and publishes the PUBLIC bundle to the directory. The private keys are
  // returned once (over TLS, never persisted server-side) for the client to seal
  // locally. Returns { subject, certificate, privateKeys: { kem, sig }, ... }.
  provisionIdentity(subject) {
    return this._post('/v1/identity/provision', { subject });
  }

  // Seal a plaintext body to a recipient. Returns the PQC envelope.
  seal({ from, to, plaintext, recipientKemPublicKey, senderSigPrivateKey }) {
    return this._post('/v1/seal', {
      from, to,
      created: Math.floor(Date.now() / 1000),
      plaintext,
      recipientKemPublicKey,
      senderSigPrivateKey,
    });
  }

  // Open (verify + decrypt) a PQC envelope.
  open({ envelope, recipientKemPrivateKey, senderSigPublicKey }) {
    return this._post('/v1/open', { envelope, recipientKemPrivateKey, senderSigPublicKey });
  }

  suites() { return this._get('/v1/suites'); }
}
